import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { GET_EXPORT_DATA } from '@/modules/API/export/getExportData'
import { downloadBase64File } from '@/modules/utils/downloader'
import { ExportDataType } from '@/types/Export'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import Loading from '@/components/Loading/Loading'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { MealType } from '@/types/Meal'
import styles from './ExportMealDialog.module.scss'

interface ExportMealDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    date: string
    sections: { sectionId: number; sectionName: string }[]
    preSelectedSectionIds: number[]
    isRpeOrLaep: boolean
    availableMealTypes: MealType[]
    mealTypeTranslations: Record<string, string>
    preSelectedMealTypes: string[]
}

const ExportMealDialog: FunctionComponent<ExportMealDialogProps> = ({
    open,
    handleClose,
    daycareId,
    date,
    sections,
    preSelectedSectionIds,
    isRpeOrLaep,
    availableMealTypes,
    mealTypeTranslations,
    preSelectedMealTypes
}: ExportMealDialogProps) => {
    const { t } = useTranslation()

    const [selectedSections, setSelectedSections] = useState<string[]>(
        preSelectedSectionIds.map((id: number): string => id.toString())
    )

    const [selectedMealTypes, setSelectedMealTypes] = useState<string[]>(
        preSelectedMealTypes.length > 0 ? preSelectedMealTypes : availableMealTypes
    )

    const [getExportData, { data: dataExport, loading: loadingExport }] = useLazyQuery(GET_EXPORT_DATA, {
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataExport?.getExportData?.base64Data) {
            downloadBase64File(
                dataExport.getExportData.base64Data,
                `repas_${date}.xlsx`,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ).then(() => {
                handleClose()
            })
        }
    }, [dataExport])

    function handleExport(): void {
        getExportData({
            variables: {
                daycareIds: [daycareId],
                from: date,
                to: date,
                type: ExportDataType.MealDay,
                sectionIds: selectedSections.map((s: string): number => Number(s)),
                mealTypes: selectedMealTypes
            }
        })
    }

    const selectOptionsSections: SelectOptionsDeprecated = [
        {
            options: sections.map(
                (section): SelectOptionDeprecated => ({
                    value: section.sectionId.toString(),
                    name: section.sectionName
                })
            )
        }
    ]

    const selectOptionsMealTypes: SelectOptionsDeprecated = [
        {
            options: availableMealTypes.map(
                (type: MealType): SelectOptionDeprecated => ({
                    value: type,
                    name: mealTypeTranslations[type] || type
                })
            )
        }
    ]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('exportMeals')} />
            <DialogBody>
                <div className={styles.DialogBody}>
                    {loadingExport ? (
                        <Loading overall={false} />
                    ) : (
                        <div className={styles.SelectContainer}>
                            <SelectMultiple
                                label={t(isRpeOrLaep ? 'mySectors' : 'mySections')}
                                selectOptions={selectOptionsSections}
                                selectValues={selectedSections}
                                setSelectValues={setSelectedSections}
                                className={styles.UserInput}
                            />
                            {availableMealTypes.length > 1 && (
                                <SelectMultiple
                                    label={t('mealType')}
                                    selectOptions={selectOptionsMealTypes}
                                    selectValues={selectedMealTypes}
                                    setSelectValues={setSelectedMealTypes}
                                    className={styles.UserInput}
                                />
                            )}
                        </div>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={'export'}
                            onClick={handleExport}
                            className={styles.Button}
                            disabled={loadingExport || selectedSections.length === 0 || selectedMealTypes.length === 0}
                        />
                    </div>
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ExportMealDialog
